/**
 * All brukervendt tekst i søkervisningen, samlet ett sted.
 *
 * Modulen er portert fra kraveditoren (SertSlay), der tekstene stod som
 * strenger. Strukturen er beholdt — én nøkkel per tekst — men verdiene er
 * byttet ut med meldings-id-er, og settes sammen av `buildUserViewTexts()` med
 * `react-intl` sin `formatMessage`. Ingen streng ligger gjemt inne i en
 * komponent, og modulen er fortsatt fri for JSX og React.
 *
 * Tekstene holdes korte med vilje. De gjentar seg nedover en hel side med krav,
 * og alt som er lengre enn det som må sies blir til støy.
 */

import { UserStatus } from './userStatus';

/**
 * Statusordene. Ett ord der det går. Én felles tabell for både krav-rader,
 * grupper og alternativer — samme status skal hete det samme overalt.
 *
 * Ordene står ikke **synlig** på raden: flatefargen og ansiktet sier det samme
 * raskere, og etiketten gjentok seg på hver eneste linje. De vises som visuelt
 * skjult tekst, først i radens linje, og er dermed statusens eneste bærer for
 * den som ikke ser fargen — se `UserStatusText` i `UserStatusBadge.tsx`. De
 * skal derfor aldri fjernes fra visningen, bare fra synsfeltet.
 */
export const STATUS_MESSAGE_IDS: { [key in UserStatus]: string } = {
  fulfilled: 'certreq.userview.status.fulfilled',
  uploaded: 'certreq.userview.status.uploaded',
  missing: 'certreq.userview.status.missing',
  notApplicable: 'certreq.userview.status.notapplicable',
};

/**
 * Statusene som ansikter — radens synlige statusbærer.
 *
 * Ansiktet står alene i radens venstre kolonne, i stedet for et statusmerke med
 * farge, ansikt og ord. Det er derfor det **eneste** øyet får, og er skalert opp
 * deretter.
 *
 * De er likevel **dekorasjon** i tilgjengelighetens forstand: emojien merkes
 * `aria-hidden`, og statusordet ligger visuelt skjult på raden ved siden av,
 * slik at en skjermleser leser «Mangler» og ikke «face with diagonal mouth».
 *
 * Tonen er med vilje mild. Et krav som ikke er oppfylt er ikke en feil brukeren
 * har gjort, bare noe som gjenstår, og ansiktet skal si «her er det litt igjen»
 * — ikke «dette gikk galt». Derfor 🫤 og ikke et gråtende eller utmattet fjes.
 *
 * `notApplicable` har med vilje ingen: en vei brukeren ikke følger skal være
 * stille, ikke kommentert.
 */
export const STATUS_EMOJI: { [key in UserStatus]: string } = {
  fulfilled: '😀',
  // Dokumentasjonen er levert, og brukeren venter på svar.
  uploaded: '😍',
  missing: '🫤',
  notApplicable: '',
};

/**
 * Har statusen et ansikt å vise?
 *
 * Den tomme strengen på `notApplicable` er et **valg**, ikke et hull, og
 * spørsmålet stilles flere steder i visningen: både om ansiktet skal tegnes og
 * om raden skal reservere plass til det. Regelen står her, sammen med tabellen
 * den handler om, slik at ingen visning finner på å tolke tomstrengen selv.
 */
export const hasStatusEmoji = (status: UserStatus): boolean => STATUS_EMOJI[status].length > 0;

/**
 * Meldings-id-ene for resten av visningen. Strukturen følger SertSlays
 * `USER_VIEW_TEXTS`, én nøkkel per tekst.
 */
export const USER_VIEW_MESSAGE_IDS = {
  /** Hovedoverskriften. Tar `{certificateName}`. */
  heading: 'certreq.userview.heading',

  /** Overskrift over alternativboksene. Står én gang, over alle. */
  alternativesHeading: 'certreq.userview.alternatives.heading',

  /**
   * Reservetekst på en gruppe der brukeren kan velge mellom kravene. Brukes
   * bare når kildedataene ikke har noen tekst i det hele tatt, så linja ikke
   * blir stående tom.
   */
  atLeastOneOf: 'certreq.userview.atleastoneof',
  exactlyOneOf: 'certreq.userview.exactlyoneof',

  /**
   * Logikken bak en gruppe, vist først når brukeren **ekspanderer** den. Den
   * kollapsede linja står uten: der er gruppen én ting å forholde seg til.
   */
  groupLogicAllOf: 'certreq.userview.grouplogic.allof',
  groupLogicAtLeastOneOf: 'certreq.userview.grouplogic.atleastoneof',
  groupLogicExactlyOneOf: 'certreq.userview.grouplogic.exactlyoneof',

  /**
   * Opplastingsknappen på et krav eller en gruppe. Den sier hele handlingen —
   * «Last opp dokumentasjon», ikke bare «Last opp». Ett krav kan dokumenteres
   * med flere filer, så knappen skifter tekst etter den første.
   */
  upload: 'certreq.userview.upload',
  addFile: 'certreq.userview.addfile',
  /** Tilgjengelig navn på filvelgeren. Tar `{title}`. */
  uploadFor: 'certreq.userview.uploadfor',
  /** Tilgjengelig navn på lista over filene som er lagt ved kravet. Tar `{title}`. */
  uploadedFilesFor: 'certreq.userview.uploadedfilesfor',
  /** Fjern-knappen ved den enkelte fila. Tar `{fileName}`. */
  removeFile: 'certreq.userview.removefile',

  /**
   * Erklæringskrav — avkryssingen som erstatter opplastingen. Teksten søkeren
   * bekrefter står allerede på raden; boksen sier bare hva avkryssingen betyr.
   */
  declarationConfirm: 'certreq.userview.declaration.confirm',
  /** Tilgjengelig navn på avkryssingsboksen. Tar `{title}`. */
  declarationConfirmFor: 'certreq.userview.declaration.confirmfor',
  /** Søknaden er ikke lenger en kladd, og erklæringen kan ikke endres. */
  declarationLocked: 'certreq.userview.declaration.locked',
  /** Lagringen feilet. */
  declarationError: 'certreq.userview.declaration.error',
  /** Kravet mangler kravidentifikator og kan ikke bekreftes. */
  declarationMissingKey: 'certreq.userview.declaration.missingkey',

  /** Vis/skjul kravene under en gruppe. Tar `{title}`. */
  expandGroup: 'certreq.userview.expandgroup',
  collapseGroup: 'certreq.userview.collapsegroup',

  /** Fremheving av veien brukeren kvalifiserer via. */
  yourPath: 'certreq.userview.yourpath',

  /**
   * Har brukeren en vei som bærer — oppfylt eller dokumentert — legges de
   * øvrige bort, og knappen henter dem fram igjen.
   */
  showOtherAlternatives: 'certreq.userview.showother',
  hideOtherAlternatives: 'certreq.userview.hideother',

  /** Reservenavn når kravdataene mangler tekst. `{number}` på alternativet. */
  untitledRequirement: 'certreq.userview.untitled',
  alternativeFallback: 'certreq.userview.alternativefallback',

  /** Tomtilstand. */
  noRequirements: 'certreq.userview.norequirements',
} as const;

/**
 * De ferdig oversatte tekstene visningen og modellen bruker.
 *
 * Modellen tar imot dette objektet i stedet for å kjenne `react-intl`: den skal
 * kunne bygges og testes uten et språkoppsett rundt seg.
 */
export type UserViewTexts = {
  statusTexts: { [key in UserStatus]: string };
  heading: (certificateName: string) => string;
  alternativesHeading: string;
  atLeastOneOf: string;
  exactlyOneOf: string;
  groupLogicAllOf: string;
  groupLogicAtLeastOneOf: string;
  groupLogicExactlyOneOf: string;
  upload: string;
  addFile: string;
  uploadFor: (title: string) => string;
  uploadedFilesFor: (title: string) => string;
  removeFile: (fileName: string) => string;
  declarationConfirm: string;
  declarationConfirmFor: (title: string) => string;
  declarationLocked: string;
  declarationError: string;
  declarationMissingKey: string;
  expandGroup: (title: string) => string;
  collapseGroup: (title: string) => string;
  yourPath: string;
  showOtherAlternatives: string;
  hideOtherAlternatives: string;
  untitledRequirement: string;
  alternativeFallback: (number: number) => string;
  noRequirements: string;
};

/** Én oversetter: `(id, verdier) => tekst`. Fylles av `useUserViewTexts`. */
export type FormatUserViewMessage = (id: string, values?: { [key: string]: any }) => string;

/** Setter sammen tekstobjektet fra meldings-id-ene. */
export const buildUserViewTexts = (format: FormatUserViewMessage): UserViewTexts => ({
  statusTexts: {
    fulfilled: format(STATUS_MESSAGE_IDS.fulfilled),
    uploaded: format(STATUS_MESSAGE_IDS.uploaded),
    missing: format(STATUS_MESSAGE_IDS.missing),
    notApplicable: format(STATUS_MESSAGE_IDS.notApplicable),
  },
  heading: (certificateName: string) => format(USER_VIEW_MESSAGE_IDS.heading, { certificateName }),
  alternativesHeading: format(USER_VIEW_MESSAGE_IDS.alternativesHeading),
  atLeastOneOf: format(USER_VIEW_MESSAGE_IDS.atLeastOneOf),
  exactlyOneOf: format(USER_VIEW_MESSAGE_IDS.exactlyOneOf),
  groupLogicAllOf: format(USER_VIEW_MESSAGE_IDS.groupLogicAllOf),
  groupLogicAtLeastOneOf: format(USER_VIEW_MESSAGE_IDS.groupLogicAtLeastOneOf),
  groupLogicExactlyOneOf: format(USER_VIEW_MESSAGE_IDS.groupLogicExactlyOneOf),
  upload: format(USER_VIEW_MESSAGE_IDS.upload),
  addFile: format(USER_VIEW_MESSAGE_IDS.addFile),
  uploadFor: (title: string) => format(USER_VIEW_MESSAGE_IDS.uploadFor, { title }),
  uploadedFilesFor: (title: string) => format(USER_VIEW_MESSAGE_IDS.uploadedFilesFor, { title }),
  removeFile: (fileName: string) => format(USER_VIEW_MESSAGE_IDS.removeFile, { fileName }),
  declarationConfirm: format(USER_VIEW_MESSAGE_IDS.declarationConfirm),
  declarationConfirmFor: (title: string) =>
    format(USER_VIEW_MESSAGE_IDS.declarationConfirmFor, { title }),
  declarationLocked: format(USER_VIEW_MESSAGE_IDS.declarationLocked),
  declarationError: format(USER_VIEW_MESSAGE_IDS.declarationError),
  declarationMissingKey: format(USER_VIEW_MESSAGE_IDS.declarationMissingKey),
  expandGroup: (title: string) => format(USER_VIEW_MESSAGE_IDS.expandGroup, { title }),
  collapseGroup: (title: string) => format(USER_VIEW_MESSAGE_IDS.collapseGroup, { title }),
  yourPath: format(USER_VIEW_MESSAGE_IDS.yourPath),
  showOtherAlternatives: format(USER_VIEW_MESSAGE_IDS.showOtherAlternatives),
  hideOtherAlternatives: format(USER_VIEW_MESSAGE_IDS.hideOtherAlternatives),
  untitledRequirement: format(USER_VIEW_MESSAGE_IDS.untitledRequirement),
  alternativeFallback: (number: number) =>
    format(USER_VIEW_MESSAGE_IDS.alternativeFallback, { number }),
  noRequirements: format(USER_VIEW_MESSAGE_IDS.noRequirements),
});
